/**
 * BackorderDetailView - Unified Backorder Detail Modal
 * 
 * Used across Hospital Dashboard (Table & Pipeline) and Admin Dashboard (Table & Pipeline)
 * Shows: Item Info, Request Info, Notes, Tracking (if shipped), Quotes Received
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShoppingCart,
  RefreshCw,
  DollarSign,
  Package,
  ExternalLink,
  Loader2,
  Send,
  MessageSquare,
  Clock,
  Mail,
} from "lucide-react";
import QuoteCard, { QuoteCardList } from "@/components/QuoteCard";
import SourceFromSuppliersModal from "@/components/admin/SourceFromSuppliersModal";
import HospitalBackorderTimeline from "@/components/HospitalBackorderTimeline";
import { toast } from "sonner";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Status badge colors
const STATUS_COLORS = {
  submitted: "bg-slate-100 text-slate-600",
  sent_to_suppliers: "bg-blue-100 text-blue-700",
  quotes_received: "bg-purple-100 text-purple-700",
  pending_approval: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  ordered: "bg-cyan-100 text-cyan-700",
  shipped: "bg-indigo-100 text-indigo-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-600"
};

export default function BackorderDetailView({
  open,
  onClose,
  backorder,
  viewMode = "hospital", // "hospital" or "admin"
  token,
  onRefresh
}) {
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [notes, setNotes] = useState([]);
  const [showSourcing, setShowSourcing] = useState(false);

  // Fetch quotes when modal opens
  useEffect(() => {
    if (open && backorder?.id) {
      fetchQuotes();
      // Load notes from backorder history
      const backorderNotes = (backorder.history || [])
        .filter(h => h.action === "note_added" || h.notes)
        .map(h => ({
          text: h.notes || h.details,
          timestamp: h.timestamp,
          by: h.by
        }));
      setNotes(backorderNotes);
    }
  }, [open, backorder?.id]);

  const fetchQuotes = async () => {
    if (!backorder?.id || !token) return;
    
    setLoadingQuotes(true);
    try {
      // Use the appropriate endpoint based on view mode
      const endpoint = viewMode === "hospital" 
        ? `${API}/api/backorders/hospital/requests/${backorder.id}/quotes`
        : `${API}/api/backorders/requests/${backorder.id}/quotes`;
      
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Flatten quotes from items
        const allQuotes = [];
        if (data.items) {
          Object.values(data.items).forEach(itemData => {
            if (itemData.quotes) {
              allQuotes.push(...itemData.quotes);
            }
          });
        }
        // Also check for direct quotes array or supplier_quotes
        if (data.quotes) {
          allQuotes.push(...data.quotes);
        }
        if (backorder.supplier_quotes) {
          // Add embedded quotes that might not be in API response
          backorder.supplier_quotes.forEach(sq => {
            const exists = allQuotes.some(q => 
              q.supplier_id === sq.supplier_id && q.submitted_at === sq.submitted_at
            );
            if (!exists) {
              allQuotes.push(sq);
            }
          });
        }
        setQuotes(allQuotes);
      }
    } catch (err) {
      console.error("Failed to fetch quotes:", err);
    } finally {
      setLoadingQuotes(false);
    }
  };

  // Add a new note
  const handleAddNote = async () => {
    if (!newNote.trim() || !backorder?.id || addingNote) return;
    
    setAddingNote(true);
    try {
      const res = await fetch(`${API}/api/backorders/requests/${backorder.id}/notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ note: newNote.trim() })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Add note to local state
        setNotes(prev => [...prev, {
          text: newNote.trim(),
          timestamp: new Date().toISOString(),
          by: "You"
        }]);
        setNewNote("");
        toast.success("Note added");
        if (onRefresh) onRefresh();
      } else {
        toast.error("Failed to add note");
      }
    } catch (err) {
      console.error("Failed to add note:", err);
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  if (!backorder) return null;

  // Extract item info
  const itemInfo = backorder.items?.[0] || backorder;
  const itemName = itemInfo.name || itemInfo.item_name || itemInfo.product_name || itemInfo.description || "Item";
  const partNumber = itemInfo.part_number || itemInfo.mfr_number || "—";
  const quantity = itemInfo.quantity || backorder.quantity || 1;

  // Get status badge
  const getStatusBadge = (status) => {
    return STATUS_COLORS[status] || "bg-slate-100 text-slate-600";
  };

  // Check if quotes have been approved
  const hasApprovedQuote = quotes.some(q => 
    q.status === "approved" || q.status === "approved_by_hospital"
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-emerald-600" />
            Backorder Request Details
          </DialogTitle>
          <DialogDescription>
            {backorder.id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Item Info & Request Info - Side by Side */}
          <div className="grid grid-cols-2 gap-4">
            {/* Item Info */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Item Info</p>
              <p className="font-medium text-slate-900">{itemName}</p>
              <p className="text-sm text-slate-600">Part #: {partNumber}</p>
              <p className="text-sm text-slate-600">Qty: {quantity}</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(backorder.status)}`}>
                  {backorder.status?.replace(/_/g, " ").toUpperCase() || "PENDING"}
                </span>
                {hasApprovedQuote && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                    QUOTE APPROVED
                  </span>
                )}
                {backorder.urgency && (
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    backorder.urgency === 'urgent' ? 'bg-red-100 text-red-700' :
                    backorder.urgency === 'high' ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {backorder.urgency.toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            {/* Request Info */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Request Info</p>
              {(backorder.submitted_by || backorder.requester_name || backorder.contact_name) ? (
                <>
                  <p className="font-medium text-slate-900">
                    {backorder.submitted_by || backorder.requester_name || backorder.contact_name}
                  </p>
                  {(backorder.submitted_by_email || backorder.contact_email) && (
                    <p className="text-sm text-slate-500">
                      {backorder.submitted_by_email || backorder.contact_email}
                    </p>
                  )}
                </>
              ) : (
                <p className="font-medium text-slate-900">—</p>
              )}
              <p className="text-xs text-slate-400 mt-2">
                Created: {backorder.created_at ? new Date(backorder.created_at).toLocaleString() : "—"}
              </p>
              {backorder.hospital_name && (
                <p className="text-xs text-slate-400 mt-1">
                  Hospital: {backorder.hospital_name}
                </p>
              )}
            </div>
          </div>

          {/* Notes Section */}
          {backorder.notes && (
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs text-amber-600 uppercase tracking-wide mb-2">Notes</p>
              <p className="text-sm text-amber-800">{backorder.notes}</p>
            </div>
          )}

          {/* Tracking Information - Show when shipped/delivered */}
          {(backorder.status === "shipped" || backorder.status === "delivered") && backorder.tracking_number && (
            <div className={`p-4 rounded-lg border ${
              backorder.status === "delivered" 
                ? "bg-green-50 border-green-200" 
                : "bg-blue-50 border-blue-200"
            }`}>
              <p className={`text-xs uppercase tracking-wide mb-2 ${
                backorder.status === "delivered" ? "text-green-600" : "text-blue-600"
              }`}>
                📦 {backorder.status === "delivered" ? "Delivery Complete" : "Shipment Tracking"}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Carrier</p>
                  <p className="font-medium text-slate-900">{backorder.carrier || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Tracking Number</p>
                  <p className="font-medium text-slate-900 font-mono text-sm">{backorder.tracking_number}</p>
                </div>
              </div>
              {backorder.tracking_url && (
                <a 
                  href={backorder.tracking_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 mt-3 px-4 py-2 rounded-lg text-sm font-medium text-white ${
                    backorder.status === "delivered" 
                      ? "bg-green-600 hover:bg-green-700" 
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  <ExternalLink className="w-4 h-4" />
                  Track Package on {backorder.carrier}
                </a>
              )}
              {backorder.shipped_at && (
                <p className="text-xs text-slate-400 mt-2">
                  Shipped: {new Date(backorder.shipped_at).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Quotes Received Section */}
          {/* Hospital fulfillment timeline — Amazon-style milestone feed */}
          {viewMode === "hospital" && token && (
            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-emerald-600" />
                Fulfillment Timeline
              </h3>
              <HospitalBackorderTimeline backorderId={backorder.id} token={token} />
            </div>
          )}

          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-600" />
                Quotes Received ({quotes.length})
              </h3>
              <div className="flex items-center gap-2">
                {viewMode === "admin" && (
                  <Button
                    size="sm"
                    onClick={() => setShowSourcing(true)}
                    className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5 h-8"
                    data-testid="bo-source-from-suppliers"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Source from Suppliers
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchQuotes}
                  disabled={loadingQuotes}
                  className="text-slate-500 hover:text-slate-700"
                >
                  {loadingQuotes ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1" />
                  )}
                  Refresh
                </Button>
              </div>
            </div>

            {loadingQuotes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : quotes.length === 0 ? (
              <div className="bg-slate-100 rounded-lg p-6 text-center">
                <Package className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p className="text-slate-500 text-sm">No quotes received yet</p>
              </div>
            ) : (
              <QuoteCardList
                quotes={quotes}
                request={backorder}
                viewMode={viewMode}
                onRefresh={fetchQuotes}
                token={token}
              />
            )}
          </div>

          {/* Notes & Updates Section */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Checkbox id="notes-section" defaultChecked />
              <label htmlFor="notes-section" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-600" />
                Notes & Updates
              </label>
            </div>

            {/* Note Input */}
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="Add a note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
                className="flex-1"
              />
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim() || addingNote}
                className="bg-blue-100 hover:bg-blue-200 text-blue-600"
                size="icon"
              >
                {addingNote ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Notes List */}
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {/* Backorder notes from 'notes' field */}
              {backorder.notes && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-sm text-slate-700">{backorder.notes}</p>
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Original note
                  </p>
                </div>
              )}
              
              {/* Dynamic notes from history */}
              {notes.map((note, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-sm text-slate-700">{note.text}</p>
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {note.by && `${note.by} • `}
                    {note.timestamp ? new Date(note.timestamp).toLocaleString() : "Just now"}
                  </p>
                </div>
              ))}
              
              {!backorder.notes && notes.length === 0 && (
                <p className="text-sm text-slate-400 italic text-center py-2">
                  No notes yet. Add one above.
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      {showSourcing && viewMode === "admin" && (
        <SourceFromSuppliersModal
          backorderId={backorder.id}
          token={token}
          onClose={() => setShowSourcing(false)}
          onBroadcast={() => {
            if (onRefresh) onRefresh();
            fetchQuotes();
          }}
        />
      )}
    </Dialog>
  );
}
